import { useEffect, useState } from "react";
import useErpDataList from "@/hooks/useErpDataList";
import ErpListToolbar from "@/components/erp/ErpListToolbar";
import ErpPagination from "@/components/erp/ErpPagination";
import ErpPageHeader from "@/components/erp/ErpPageHeader";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Building2, Layers, Palette, Warehouse, Users2, Briefcase, UserSquare2 } from "lucide-react";

const BUSINESS_TYPES = [
  { value: "fnb", label: "F&B" }, { value: "retail", label: "Retail" },
  { value: "manufacturing", label: "Manufacturing" }, { value: "services", label: "Services" },
  { value: "distribution", label: "Distribution" }, { value: "logistics", label: "Logistics" },
  { value: "workforce", label: "Workforce Service" }, { value: "rental", label: "Rental" },
];
const EMP_TYPES = [
  { value: "monthly", label: "Bulanan" }, { value: "daily", label: "Harian" },
  { value: "contract", label: "Kontrak" }, { value: "freelance", label: "Freelance" },
];

const MASTER_DATA_VALID_TABS = ['business-units', 'brands', 'warehouses', 'departments', 'positions', 'employees', 'outlets'];

export default function MasterDataPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = searchParams.get("tab");
  const [tab, setTabState] = useState(
    MASTER_DATA_VALID_TABS.includes(requestedTab) ? requestedTab : 'business-units'
  );
  useEffect(() => {
    const requested = searchParams.get("tab");
    if (MASTER_DATA_VALID_TABS.includes(requested)) setTabState(requested);
  }, [searchParams]);
  const setTab = (value) => {
    if (value === "outlets") { navigate("/outlets"); return; }
    if (!MASTER_DATA_VALID_TABS.includes(value)) return;
    setTabState(value);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("tab", value);
      return next;
    }, { replace: true });
  };
  return (
    <div className="page-shell">
      <div data-workspace-redundant-header>
        <ErpPageHeader
          title="Master Data"
          description="Fondasi organisasi MAZBOY HUB · Owner Configurable"
          breadcrumbs={["Organisasi", "Master Data"]}
        />
      </div>

      
<div className="mb-5 border-b border-slate-200">
  <nav className="flex flex-wrap gap-2 pb-3" aria-label="Modul Organisasi">
    {[
      ["business-units", "Business Unit"],
      ["brands", "Brand"],
      ["departments", "Departemen"],
      ["positions", "Jabatan"],
      ["outlets", "Outlet"],
      ["warehouses", "Gudang"],
    ].map(([value, label]) => (
      <button
        key={value}
        type="button"
        onClick={() => setTab(value)}
        className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
          tab === value
            ? "bg-blue-900 text-white shadow-sm"
            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
        }`}
      >
        {label}
      </button>
    ))}
  </nav>
</div>

<div className="master-data-module">
        {tab === "business-units" && <BusinessUnitsTab />}
        {tab === "brands" && <BrandsTab />}
        {tab === "warehouses" && <WarehousesTab />}
        {tab === "departments" && <DepartmentsTab />}
        {tab === "positions" && <PositionsTab />}
        {tab === "employees" && <EmployeesTab />}
      </div>
    </div>
  );
}

// ---- Business Units ----
function BusinessUnitsTab() {
  const [items, setItems] = useState([]);
  const list = useErpDataList({ items, searchFields: ["id", "name", "business_type"], pageSize: 10 });
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", business_type: "fnb", active: true });
  const load = () => api.get("/business-units").then(({data}) => setItems(data));
  useEffect(() => { load(); }, []);

  function startEdit(item) {
    setEditId(item.id);
    setForm({
      name: item.name || "",
      business_type: item.business_type || "fnb",
      active: item.active !== false,
    });
    setOpen(true);
  }
async function save() {
    try { await (editId ? api.put("/business-units/" + editId, form) : api.post("/business-units", form)); toast.success(editId ? "Business Unit diperbarui" : "Business Unit dibuat"); setOpen(false); setEditId(null); setForm({name:"", business_type:"fnb", active:true}); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base">{items.length} Business Unit</CardTitle>
        <Button data-testid="bu-add" onClick={() => { setEditId(null); setForm({name:"", business_type:"fnb", active:true}); setOpen(true); }} size="sm" className="bg-orange-500 hover:bg-orange-600"><Plus className="w-4 h-4 mr-1"/>Tambah</Button>
      </CardHeader>
      <CardContent>
        <ErpListToolbar searchValue={list.search} onSearchChange={list.setSearch} searchPlaceholder="Cari data..." resultCount={list.totalItems} onReset={list.reset} />
        <Table>
          <TableHeader><TableRow><TableHead>ID</TableHead><TableHead>Nama</TableHead><TableHead>Tipe Bisnis</TableHead><TableHead>Status</TableHead><TableHead>Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {list.paginatedItems.map(i => (
              <TableRow key={i.id}>
                <TableCell className="font-mono text-xs"><button type="button" className="text-blue-600 hover:underline" title="Salin ID" onClick={() => { navigator.clipboard.writeText(i.id).then(() => toast.success("ID disalin")).catch(() => toast.error("Gagal menyalin ID")); }}>{i.id}</button></TableCell>
                <TableCell className="font-medium">{i.name}</TableCell>
                <TableCell><Badge variant="outline">{BUSINESS_TYPES.find(x=>x.value===i.business_type)?.label || i.business_type}</Badge></TableCell>
                <TableCell>{i.active ? <Badge className="bg-emerald-500">Aktif</Badge> : <Badge variant="destructive">Non-aktif</Badge>}</TableCell>
              <TableCell><Button type="button" variant="outline" size="sm" onClick={() => startEdit(i)}>Edit</Button></TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
        <ErpPagination page={list.page} totalPages={list.totalPages} totalItems={list.totalItems} pageSize={list.pageSize} onPageChange={list.setPage} />
      </CardContent>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Edit Business Unit" : "Business Unit Baru"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama</Label><Input data-testid="bu-form-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></div>
            <div><Label>Tipe Bisnis</Label>
              <Select value={form.business_type} onValueChange={v=>setForm({...form, business_type:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{BUSINESS_TYPES.map(b=><SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button><Button data-testid="bu-form-submit" onClick={save} className="bg-orange-500 hover:bg-orange-600">{editId ? "Perbarui" : "Simpan"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ---- Brands ----
function BrandsTab() {
  const [items, setItems] = useState([]);
  const list = useErpDataList({ items, searchFields: ["id", "name", "business_unit_id"], pageSize: 10 });
  const [bus, setBus] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ business_unit_id: "", name: "", primary_color: "#F97316", active: true });
  const load = () => api.get("/brands").then(({data})=>setItems(data));
  useEffect(() => {
    api.get("/business-units").then(({data})=>{ setBus(data); setForm(f=>({...f, business_unit_id: data[0]?.id || ""})); });
    load();
  }, []);

  function startEdit(item) {
    setEditId(item.id);
    setForm({business_unit_id: item.business_unit_id || "", name: item.name || "", primary_color: item.primary_color || "#F97316", active: item.active !== false});
    setOpen(true);
  }
async function save() {
    try { await (editId ? api.put("/brands/" + editId, form) : api.post("/brands", form)); toast.success(editId ? "Brand diperbarui" : "Brand dibuat"); setOpen(false); setEditId(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }
  const buName = (id) => bus.find(b=>b.id===id)?.name || "-";
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base">{items.length} Brand</CardTitle>
        <Button data-testid="brand-add" onClick={() => { setEditId(null); setForm({business_unit_id:"", name:"", primary_color:"#F97316", active:true}); setOpen(true); }} size="sm" className="bg-orange-500 hover:bg-orange-600"><Plus className="w-4 h-4 mr-1"/>Tambah</Button>
      </CardHeader>
      <CardContent>
        <ErpListToolbar searchValue={list.search} onSearchChange={list.setSearch} searchPlaceholder="Cari data..." resultCount={list.totalItems} onReset={list.reset} />
        <Table>
          <TableHeader><TableRow><TableHead>ID</TableHead><TableHead>Brand</TableHead><TableHead>Business Unit</TableHead><TableHead>Warna</TableHead><TableHead>Status</TableHead><TableHead>Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {list.paginatedItems.map(i => (
              <TableRow key={i.id}>
                <TableCell className="font-mono text-xs"><button type="button" className="text-blue-600 hover:underline" title="Salin ID" onClick={() => { navigator.clipboard.writeText(i.id).then(() => toast.success("ID disalin")).catch(() => toast.error("Gagal menyalin ID")); }}>{i.id}</button></TableCell>
                <TableCell className="font-medium">{i.name}</TableCell>
                <TableCell className="text-xs">{buName(i.business_unit_id)}</TableCell>
                <TableCell><div className="flex items-center gap-2"><div className="w-4 h-4 rounded-full" style={{background: i.primary_color}}/><span className="font-mono text-xs">{i.primary_color}</span></div></TableCell>
                <TableCell>{i.active ? <Badge className="bg-emerald-500">Aktif</Badge> : <Badge variant="destructive">Non-aktif</Badge>}</TableCell>
              <TableCell><Button type="button" variant="outline" size="sm" onClick={() => startEdit(i)}>Edit</Button></TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
        <ErpPagination page={list.page} totalPages={list.totalPages} totalItems={list.totalItems} pageSize={list.pageSize} onPageChange={list.setPage} />
      </CardContent>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Edit Brand" : "Brand Baru"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Business Unit</Label>
              <Select value={form.business_unit_id} onValueChange={v=>setForm({...form, business_unit_id:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent className="max-h-[240px] overflow-y-auto" position="popper">{bus.map(b=><SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Nama Brand</Label><Input data-testid="brand-form-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></div>
            <div><Label>Warna (hex)</Label><Input value={form.primary_color} onChange={e=>setForm({...form, primary_color:e.target.value})}/></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button><Button data-testid="brand-form-submit" onClick={save} className="bg-orange-500 hover:bg-orange-600">{editId ? "Perbarui" : "Simpan"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ---- Warehouses ----
function WarehousesTab() {
  const [items, setItems] = useState([]);
  const list = useErpDataList({ items, searchFields: ["id", "name", "kind", "business_unit_id"], pageSize: 10 });
  const [bus, setBus] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ business_unit_id: "", name: "", kind: "central", outlet_id: null, active: true });
  const load = () => api.get("/warehouses").then(({data})=>setItems(data));
  useEffect(() => {
    api.get("/business-units").then(({data})=>{ setBus(data); setForm(f=>({...f, business_unit_id: data[0]?.id || ""})); });
    api.get("/outlets").then(({data})=>setOutlets(data));
    load();
  }, []);

  function startEdit(item) {
    setEditId(item.id);
    setForm({business_unit_id: item.business_unit_id || "", name: item.name || "", kind: item.kind || "central", outlet_id: item.outlet_id || null, active: item.active !== false});
    setOpen(true);
  }
async function save() {
    try { await (editId ? api.put("/warehouses/" + editId, form) : api.post("/warehouses", form)); toast.success(editId ? "Warehouse diperbarui" : "Warehouse dibuat"); setOpen(false); setEditId(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base">{items.length} Warehouse</CardTitle>
        <Button data-testid="warehouse-add" onClick={() => { setEditId(null); setForm({business_unit_id:"", name:"", kind:"central", outlet_id:null, active:true}); setOpen(true); }} size="sm" className="bg-orange-500 hover:bg-orange-600"><Plus className="w-4 h-4 mr-1"/>Tambah</Button>
      </CardHeader>
      <CardContent>
        <ErpListToolbar searchValue={list.search} onSearchChange={list.setSearch} searchPlaceholder="Cari data..." resultCount={list.totalItems} onReset={list.reset} />
        <Table>
          <TableHeader><TableRow><TableHead>ID</TableHead><TableHead>Nama</TableHead><TableHead>Tipe</TableHead><TableHead>Business Unit</TableHead><TableHead>Status</TableHead><TableHead>Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {list.paginatedItems.map(i => (
              <TableRow key={i.id}>
                <TableCell className="font-mono text-xs"><button type="button" className="text-blue-600 hover:underline" title="Salin ID" onClick={() => { navigator.clipboard.writeText(i.id).then(() => toast.success("ID disalin")).catch(() => toast.error("Gagal menyalin ID")); }}>{i.id}</button></TableCell>
                <TableCell className="font-medium">{i.name}</TableCell>
                <TableCell><Badge variant="outline">{i.kind.toUpperCase()}</Badge></TableCell>
                <TableCell className="text-xs">{bus.find(b=>b.id===i.business_unit_id)?.name || "-"}</TableCell>
                <TableCell>{i.active ? <Badge className="bg-emerald-500">Aktif</Badge> : <Badge variant="destructive">Non-aktif</Badge>}</TableCell>
              <TableCell><Button type="button" variant="outline" size="sm" onClick={() => startEdit(i)}>Edit</Button></TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
        <ErpPagination page={list.page} totalPages={list.totalPages} totalItems={list.totalItems} pageSize={list.pageSize} onPageChange={list.setPage} />
      </CardContent>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Edit Warehouse" : "Warehouse Baru"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama</Label><Input data-testid="warehouse-form-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></div>
            <div><Label>Business Unit</Label>
              <Select value={form.business_unit_id} onValueChange={v=>setForm({...form, business_unit_id:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent className="max-h-[240px] overflow-y-auto" position="popper">{bus.map(b=><SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Tipe</Label>
              <Select value={form.kind} onValueChange={v=>setForm({...form, kind:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="central">Central (Gudang Pusat)</SelectItem>
                  <SelectItem value="micro">Micro (Toko sebagai gudang)</SelectItem>
                  <SelectItem value="outlet">Outlet Stock</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.kind !== "central" && (
              <div><Label>Outlet Terkait</Label>
                <Select value={form.outlet_id || ""} onValueChange={v=>setForm({...form, outlet_id:v})}>
                  <SelectTrigger><SelectValue placeholder="Pilih outlet..."/></SelectTrigger>
                  <SelectContent>{outlets.map(o=><SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button><Button data-testid="warehouse-form-submit" onClick={save} className="bg-orange-500 hover:bg-orange-600">{editId ? "Perbarui" : "Simpan"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ---- Departments ----
function DepartmentsTab() {
  const [items, setItems] = useState([]);
  const list = useErpDataList({ items, searchFields: ["id", "name"], pageSize: 10 });
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", parent_id: null });
  const load = () => api.get("/departments").then(({data})=>setItems(data));
  useEffect(() => { load(); }, []);

  function startEdit(item) {
    setEditId(item.id);
    setForm({name: item.name || "", parent_id: item.parent_id || null});
    setOpen(true);
  }
async function save() {
    try { await (editId ? api.put("/departments/" + editId, form) : api.post("/departments", form)); toast.success(editId ? "Department diperbarui" : "Department dibuat"); setOpen(false); setEditId(null); setForm({name:"", parent_id:null}); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base">{items.length} Department</CardTitle>
        <Button data-testid="dept-add" onClick={() => { setEditId(null); setForm({name:"", parent_id:null}); setOpen(true); }} size="sm" className="bg-orange-500 hover:bg-orange-600"><Plus className="w-4 h-4 mr-1"/>Tambah</Button>
      </CardHeader>
      <CardContent>
        <ErpListToolbar searchValue={list.search} onSearchChange={list.setSearch} searchPlaceholder="Cari data..." resultCount={list.totalItems} onReset={list.reset} />
        <Table>
          <TableHeader><TableRow><TableHead>ID</TableHead><TableHead>Nama</TableHead><TableHead>Parent</TableHead><TableHead>Status</TableHead><TableHead>Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {list.paginatedItems.map(i => (
              <TableRow key={i.id}>
                <TableCell className="font-mono text-xs"><button type="button" className="text-blue-600 hover:underline" title="Salin ID" onClick={() => { navigator.clipboard.writeText(i.id).then(() => toast.success("ID disalin")).catch(() => toast.error("Gagal menyalin ID")); }}>{i.id}</button></TableCell>
                <TableCell className="font-medium">{i.name}</TableCell>
                <TableCell className="text-xs">{items.find(x=>x.id===i.parent_id)?.name || "—"}</TableCell>
                <TableCell>{i.active !== false ? <Badge className="bg-emerald-500">Aktif</Badge> : <Badge variant="destructive">Non-aktif</Badge>}</TableCell>
              <TableCell><Button type="button" variant="outline" size="sm" onClick={() => startEdit(i)}>Edit</Button></TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
        <ErpPagination page={list.page} totalPages={list.totalPages} totalItems={list.totalItems} pageSize={list.pageSize} onPageChange={list.setPage} />
      </CardContent>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Edit Department" : "Department Baru"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama</Label><Input data-testid="dept-form-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button><Button data-testid="dept-form-submit" onClick={save} className="bg-orange-500 hover:bg-orange-600">{editId ? "Perbarui" : "Simpan"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ---- Positions ----
function PositionsTab() {
  const [items, setItems] = useState([]);
  const list = useErpDataList({ items, searchFields: ["id", "title", "department_id"], pageSize: 10 });
  const [depts, setDepts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ department_id: "", title: "", level: 1 });
  const load = () => api.get("/positions").then(({data})=>setItems(data));
  useEffect(() => {
    api.get("/departments").then(({data})=>{ setDepts(data); setForm(f=>({...f, department_id: data[0]?.id || ""})); });
    load();
  }, []);

  function startEdit(item) {
    setEditId(item.id);
    setForm({department_id: item.department_id || "", title: item.title || "", level: Number(item.level) || 1});
    setOpen(true);
  }
async function save() {
    try { await (editId ? api.put("/positions/" + editId, form) : api.post("/positions", form)); toast.success(editId ? "Position diperbarui" : "Position dibuat"); setOpen(false); setEditId(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  }
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base">{items.length} Position</CardTitle>
        <Button data-testid="pos-add" onClick={() => { setEditId(null); setForm({department_id:"", title:"", level:1}); setOpen(true); }} size="sm" className="bg-orange-500 hover:bg-orange-600"><Plus className="w-4 h-4 mr-1"/>Tambah</Button>
      </CardHeader>
      <CardContent>
        <ErpListToolbar searchValue={list.search} onSearchChange={list.setSearch} searchPlaceholder="Cari data..." resultCount={list.totalItems} onReset={list.reset} />
        <Table>
          <TableHeader><TableRow><TableHead>ID</TableHead><TableHead>Title</TableHead><TableHead>Department</TableHead><TableHead>Level</TableHead><TableHead>Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {list.paginatedItems.map(i => (
              <TableRow key={i.id}>
                <TableCell className="font-mono text-xs"><button type="button" className="text-blue-600 hover:underline" title="Salin ID" onClick={() => { navigator.clipboard.writeText(i.id).then(() => toast.success("ID disalin")).catch(() => toast.error("Gagal menyalin ID")); }}>{i.id}</button></TableCell>
                <TableCell className="font-medium">{i.title}</TableCell>
                <TableCell className="text-xs">{depts.find(d=>d.id===i.department_id)?.name || "-"}</TableCell>
                <TableCell>L{i.level}</TableCell>
              <TableCell><Button type="button" variant="outline" size="sm" onClick={() => startEdit(i)}>Edit</Button></TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
        <ErpPagination page={list.page} totalPages={list.totalPages} totalItems={list.totalItems} pageSize={list.pageSize} onPageChange={list.setPage} />
      </CardContent>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Edit Position" : "Position Baru"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Department</Label>
              <Select value={form.department_id} onValueChange={v=>setForm({...form, department_id:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent className="max-h-[240px] overflow-y-auto" position="popper">{depts.map(d=><SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Title (jabatan)</Label><Input data-testid="pos-form-title" value={form.title} onChange={e=>setForm({...form, title:e.target.value})}/></div>
            <div><Label>Level</Label><Input type="number" value={form.level} onChange={e=>setForm({...form, level:Number(e.target.value)})}/></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button><Button data-testid="pos-form-submit" onClick={save} className="bg-orange-500 hover:bg-orange-600">{editId ? "Perbarui" : "Simpan"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ---- Employees ----
function EmployeesTab() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountEmployee, setAccountEmployee] = useState(null);
  const [accountInfo, setAccountInfo] = useState(null);
  const [accountUsername, setAccountUsername] = useState("");
  const [accountRole, setAccountRole] = useState("cashier_outlet");
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [accountBusy, setAccountBusy] = useState(false);
  const [items, setItems] = useState([]);
  const list = useErpDataList({ items, searchFields: ["id", "name", "phone", "email", "nik"], pageSize: 10 });
  const [depts, setDepts] = useState([]);
  const [positions, setPositions] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const emptyEmployeeForm = () => ({
    name: "", phone: "", email: "", nik: "", address: "",
    emergency_contact_name: "", emergency_contact_phone: "",
    department_id: "", position_id: "", outlet_id: "",
    employment_type: "monthly", pay_scheme: "monthly",
    salary: 0, hourly_rate: 0, daily_rate: 0, weekly_rate: 0,
    attendance_required: true, attendance_used_for_payroll: true,
    status: "active"
  });
  const [form, setForm] = useState(emptyEmployeeForm);
  const load = () => api.get("/employees").then(({data})=>setItems(data));
  useEffect(() => {
    api.get("/departments").then(({data})=>setDepts(data));
    api.get("/positions").then(({data})=>setPositions(data));
    api.get("/outlets").then(({data})=>setOutlets(data));
    load();
  }, []);

  function startEdit(item) {
    setEditId(item.id);
    setForm({
      ...emptyEmployeeForm(),
      ...item,
      name: item.name || "",
      phone: item.phone || "",
      email: item.email || "",
      nik: item.nik || "",
      address: item.address || "",
      emergency_contact_name: item.emergency_contact_name || "",
      emergency_contact_phone: item.emergency_contact_phone || "",
      department_id: item.department_id || "",
      position_id: item.position_id || "",
      outlet_id: item.outlet_id || "",
      employment_type: item.employment_type || "monthly",
      pay_scheme: item.pay_scheme || (
        item.employment_type === "daily" ? "daily" : "monthly"
      ),
      salary: Number(item.salary) || 0,
      hourly_rate: Number(item.hourly_rate) || 0,
      daily_rate: Number(item.daily_rate) || 0,
      weekly_rate: Number(item.weekly_rate) || 0,
      attendance_required: item.attendance_required !== false,
      attendance_used_for_payroll: item.attendance_used_for_payroll !== false,
      status: item.status || "active"
    });
    setOpen(true);
  }
async function save() {
    const name = String(form.name || "").trim();
    const schemes = ["hourly", "daily", "weekly", "monthly"];
    const payScheme = schemes.includes(form.pay_scheme) ? form.pay_scheme : "monthly";
    const rateFields = {
      hourly: "hourly_rate",
      daily: "daily_rate",
      weekly: "weekly_rate",
      monthly: "salary"
    };
    const rateField = rateFields[payScheme];
    const rateText = String(form[rateField] ?? "").trim();
    const rate = Number(rateText);

    if (!name) {
      toast.error("Nama karyawan wajib diisi");
      return;
    }
    if (!rateText || !Number.isFinite(rate) || rate < 0) {
      toast.error("Tarif upah harus berupa angka valid dan tidak boleh negatif");
      return;
    }

    try {
      const payload = {
        ...form,
        name,
        pay_scheme: payScheme,
        [rateField]: rate,
        salary: Number(form.salary) || 0,
        hourly_rate: Number(form.hourly_rate) || 0,
        daily_rate: Number(form.daily_rate) || 0,
        weekly_rate: Number(form.weekly_rate) || 0,
        attendance_required: form.attendance_required !== false,
        attendance_used_for_payroll: form.attendance_used_for_payroll !== false
      };
      await (editId
        ? api.put("/employees/" + editId, payload)
        : api.post("/employees", payload));
      toast.success(editId ? "Karyawan diperbarui" : "Karyawan dibuat");
      setOpen(false);
      setEditId(null);
      setForm(emptyEmployeeForm());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan karyawan");
    }
  }

  async function openAccount(employee) {
    setAccountEmployee(employee);
    setAccountInfo(null);
    setAccountUsername("");
    setTemporaryPassword("");
    setAccountOpen(true);
    try {
      const { data } = await api.get(`/employees/${employee.id}/login-account`);
      setAccountInfo(data);
      setAccountUsername(String(data.username || "").replace(/@mazboy\.com$/i, ""));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat status akun");
    }
  }

  async function accountAction(action) {
    if (!accountEmployee || accountBusy) return;
    setAccountBusy(true);
    try {
      let data;
      if (action === "create") {
        ({ data } = await api.post(`/employees/${accountEmployee.id}/login-account`, {
          username: accountUsername.trim(), role: accountRole
        }));
      } else if (action === "username") {
        ({ data } = await api.put(`/employees/${accountEmployee.id}/login-account/username`, {
          username: accountUsername.trim()
        }));
      } else if (action === "reset") {
        if (!window.confirm("Reset password akun ini?")) return;
        ({ data } = await api.post(`/employees/${accountEmployee.id}/login-account/reset-password`));
      } else if (action === "disable") {
        if (!window.confirm("Nonaktifkan akun ini? Pengguna tidak dapat login.")) return;
        ({ data } = await api.post(`/employees/${accountEmployee.id}/login-account/disable`));
      }

      if (data?.temporary_password) setTemporaryPassword(data.temporary_password);

      if (action === "create") {
        setAccountInfo({
          has_account: true,
          username: data.username,
          role: data.role || accountRole,
          disabled: false
        });
        setAccountUsername(String(data.username || "").replace(/@mazboy\.com$/i, ""));
      } else if (action === "username") {
        setAccountInfo(v => ({
          ...v,
          has_account: true,
          username: data.username,
          role: data.role || v?.role || accountRole
        }));
        setAccountUsername(String(data.username || "").replace(/@mazboy\.com$/i, ""));
      } else if (action === "disable") {
        setAccountInfo(v => ({ ...v, disabled: true }));
      }

      toast.success(
        action === "create" ? "Akun berhasil dibuat" :
        action === "username" ? "Username diperbarui" :
        action === "reset" ? "Password berhasil direset" :
        "Akun dinonaktifkan"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operasi akun gagal");
    } finally {
      setAccountBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base">{items.length} Karyawan</CardTitle>
        <Button data-testid="emp-add" onClick={() => { setEditId(null); setForm(emptyEmployeeForm()); setOpen(true); }} size="sm" className="bg-orange-500 hover:bg-orange-600"><Plus className="w-4 h-4 mr-1"/>Tambah</Button>
      </CardHeader>
      <CardContent>
        <ErpListToolbar searchValue={list.search} onSearchChange={list.setSearch} searchPlaceholder="Cari data..." resultCount={list.totalItems} onReset={list.reset} />
        <Table>
          <TableHeader><TableRow>
            <TableHead>Employee ID</TableHead><TableHead>Nama</TableHead><TableHead>Kontak</TableHead>
            <TableHead>Department</TableHead><TableHead>Position</TableHead>
            <TableHead>Outlet</TableHead><TableHead>Tipe</TableHead>
            <TableHead className="text-right">Gaji</TableHead><TableHead>Status</TableHead>
          <TableHead>Akun Login</TableHead><TableHead>Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {list.paginatedItems.map(e => (
              <TableRow key={e.id}>
                <TableCell className="font-mono text-xs">
                  <button type="button" className="text-blue-600 hover:underline" title="Klik untuk menyalin Employee ID"
                    onClick={() => { navigator.clipboard.writeText(e.id); toast.success("Employee ID disalin"); }}>
                    {e.id}
                  </button>
                </TableCell>
                <TableCell className="font-medium">{e.name}</TableCell>
                <TableCell className="text-xs">{e.phone || e.email || "-"}</TableCell>
                <TableCell className="text-xs">{depts.find(d=>d.id===e.department_id)?.name || "-"}</TableCell>
                <TableCell className="text-xs">{positions.find(p=>p.id===e.position_id)?.title || "-"}</TableCell>
                <TableCell className="text-xs">{outlets.find(o=>o.id===e.outlet_id)?.name || "-"}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]">{EMP_TYPES.find(t=>t.value===e.employment_type)?.label}</Badge></TableCell>
                <TableCell className="text-right num text-xs">{(() => {
              const scheme = e.pay_scheme || (e.employment_type === "daily" ? "daily" : "monthly");
              const rates = { hourly: e.hourly_rate, daily: e.daily_rate, weekly: e.weekly_rate, monthly: e.salary };
              const units = { hourly: "/jam", daily: "/hari", weekly: "/minggu", monthly: "/bulan" };
              return `Rp${(Number(rates[scheme]) || 0).toLocaleString("id-ID")}${units[scheme] || "/bulan"}`;
            })()}</TableCell>
                <TableCell>{e.status === "active" ? <Badge className="bg-emerald-500">Aktif</Badge> : <Badge variant="destructive">{e.status}</Badge>}</TableCell>
              <TableCell>
                {isOwner && <Button type="button" variant="outline" size="sm" onClick={() => openAccount(e)}>Kelola Akun</Button>}
              </TableCell>
              <TableCell><Button type="button" variant="outline" size="sm" onClick={() => startEdit(e)}>Edit</Button></TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
        <ErpPagination page={list.page} totalPages={list.totalPages} totalItems={list.totalItems} pageSize={list.pageSize} onPageChange={list.setPage} />
      </CardContent>
      {isOwner && <Dialog open={accountOpen} onOpenChange={v => {
        setAccountOpen(v);
        if (!v) setTemporaryPassword("");
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Akun Login — {accountEmployee?.name}</DialogTitle></DialogHeader>
          {!accountInfo ? <p className="text-sm text-slate-500">Memuat status akun...</p> : <>
            {accountInfo.has_account ? <>
              <div className="space-y-2">
                <Label>Username saat ini</Label>
                <p className="text-sm font-medium break-all">{accountInfo.username}</p>
                <Label>Username baru (tanpa @mazboy.com)</Label>
                <Input value={accountUsername} onChange={e => setAccountUsername(e.target.value)} disabled={accountInfo.disabled} />
                <p className="text-xs text-slate-500">Role: {accountInfo.role || "-"} · Status: {accountInfo.disabled ? "Nonaktif" : "Aktif"}</p>
                {!accountInfo.disabled && <Button disabled={accountBusy || !accountUsername.trim()} onClick={() => accountAction("username")}>Simpan Username</Button>}
                {!accountInfo.disabled && <Button variant="outline" disabled={accountBusy} onClick={() => accountAction("reset")}>Reset Password</Button>}
                {!accountInfo.disabled && <Button variant="destructive" disabled={accountBusy} onClick={() => accountAction("disable")}>Nonaktifkan Akun</Button>}
              </div>
            </> : <>
              <div className="space-y-2">
                <Label>Username (tanpa @mazboy.com)</Label>
                <Input value={accountUsername} onChange={e => setAccountUsername(e.target.value)} placeholder="contoh: budi.kasir" />
                <Label>Role</Label>
                <select className="w-full rounded-md border p-2 text-sm" value={accountRole} onChange={e => setAccountRole(e.target.value)}>
                  <option value="manager">Manager</option>
                  <option value="finance">Finance</option>
                  <option value="marketing">Marketing</option>
                  <option value="accounting">Accounting</option>
                  <option value="hr">HR</option>
                  <option value="procurement">Procurement</option>
                  <option value="warehouse">Warehouse</option>
                  <option value="ga_it">GA / IT</option>
                  <option value="spv_outlet">Outlet Supervisor</option>
                  <option value="cashier_outlet">Cashier</option>
                  <option value="staff_outlet">Outlet Staff</option>
                </select>
                <Button disabled={accountBusy || !accountUsername.trim()} onClick={() => accountAction("create")}>Buat Akun</Button>
              </div>
            </>}
            {temporaryPassword && <div className="rounded-md border p-3 space-y-2">
              <p className="text-sm font-semibold">Password sementara — simpan sekarang</p>
              <code className="text-lg select-all">{temporaryPassword}</code>
              <Button variant="outline" onClick={() => navigator.clipboard.writeText(temporaryPassword).then(() => toast.success("Password disalin")).catch(() => toast.error("Gagal menyalin password"))}>Salin Password</Button>
              <p className="text-xs text-slate-500">Password hanya ditampilkan setelah operasi berhasil.</p>
            </div>}
          </>}
          <DialogFooter><Button variant="outline" onClick={() => { setAccountOpen(false); setTemporaryPassword(""); }}>Tutup</Button></DialogFooter>
        </DialogContent>
      </Dialog>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editId ? "Edit Karyawan" : "Karyawan Baru"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Nama</Label><Input data-testid="emp-form-name" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/></div>
            <div><Label>NIK</Label><Input value={form.nik} onChange={e=>setForm({...form, nik:e.target.value})}/></div>
            <div><Label>Alamat</Label><Input value={form.address} onChange={e=>setForm({...form, address:e.target.value})}/></div>
            <div><Label>Nama Kontak Darurat</Label><Input value={form.emergency_contact_name} onChange={e=>setForm({...form, emergency_contact_name:e.target.value})}/></div>
            <div><Label>Telepon Kontak Darurat</Label><Input value={form.emergency_contact_phone} onChange={e=>setForm({...form, emergency_contact_phone:e.target.value})}/></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})}/></div>
            <div><Label>Email</Label><Input value={form.email} onChange={e=>setForm({...form, email:e.target.value})}/></div>
            <div><Label>Department</Label>
              <Select value={form.department_id} onValueChange={v=>setForm({...form, department_id:v})}>
                <SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
                <SelectContent className="max-h-[240px] overflow-y-auto" position="popper">{depts.map(d=><SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Position</Label>
              <Select value={form.position_id} onValueChange={v=>setForm({...form, position_id:v})}>
                <SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
                <SelectContent className="max-h-[240px] overflow-y-auto" position="popper">{positions.filter(p=>!form.department_id || p.department_id===form.department_id).map(p=><SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Outlet</Label>
              <Select value={form.outlet_id} onValueChange={v=>setForm({...form, outlet_id:v})}>
                <SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
                <SelectContent>{outlets.map(o=><SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Employment Type</Label>
              <Select value={form.employment_type} onValueChange={v=>setForm({...form, employment_type:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{EMP_TYPES.map(t=><SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Skema Upah</Label>
              <Select value={form.pay_scheme} onValueChange={v=>setForm({...form, pay_scheme:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Per Jam</SelectItem>
                  <SelectItem value="daily">Per Hari</SelectItem>
                  <SelectItem value="weekly">Per Minggu</SelectItem>
                  <SelectItem value="monthly">Per Bulan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.pay_scheme === "hourly" && (
              <div className="col-span-2"><Label>Tarif per Jam (Rp)</Label><Input type="number" min="0" step="1" value={form.hourly_rate} onChange={e=>setForm({...form, hourly_rate:e.target.value})}/></div>
            )}
            {form.pay_scheme === "daily" && (
              <div className="col-span-2"><Label>Upah Harian (Rp)</Label><Input type="number" min="0" step="1" value={form.daily_rate} onChange={e=>setForm({...form, daily_rate:e.target.value})}/></div>
            )}
            {form.pay_scheme === "weekly" && (
              <div className="col-span-2"><Label>Upah Mingguan (Rp)</Label><Input type="number" min="0" step="1" value={form.weekly_rate} onChange={e=>setForm({...form, weekly_rate:e.target.value})}/></div>
            )}
            {form.pay_scheme === "monthly" && (
              <div className="col-span-2"><Label>Gaji Bulanan (Rp)</Label><Input type="number" min="0" step="1" value={form.salary} onChange={e=>setForm({...form, salary:e.target.value})}/></div>
            )}
            <div className="col-span-2 flex items-center gap-2">
              <input id="emp-attendance-required" type="checkbox" checked={form.attendance_required} onChange={e=>setForm({...form, attendance_required:e.target.checked})}/>
              <Label htmlFor="emp-attendance-required">Karyawan wajib melakukan absensi</Label>
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <input id="emp-attendance-payroll" type="checkbox" checked={form.attendance_used_for_payroll} onChange={e=>setForm({...form, attendance_used_for_payroll:e.target.checked})}/>
              <Label htmlFor="emp-attendance-payroll">Gunakan absensi dalam perhitungan payroll</Label>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button><Button data-testid="emp-form-submit" onClick={save} className="bg-orange-500 hover:bg-orange-600">{editId ? "Perbarui" : "Simpan"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
